import { useState } from 'react';
import { Badge, Button, Card, CardContent, Input, cn } from '@rolefit/ui';
import { queries, type ApplicationRow, type ReadLog, type SuggestionRow } from '../lib/queries.js';
import { useAsync } from '../lib/useAsync.js';
import { BOARD_STATES, STATE_LABELS } from '../lib/fit.js';

// Inbox auto-tracking (M1.5) review queue: scan recent mail, then accept/dismiss
// proposed forward-only state changes. Suggestions only; accept applies + logs.
function InboxSuggestions({ onApplied }: { onApplied: () => void }) {
  const sugs = useAsync(() => queries.getSuggestions(), []);
  const [scanning, setScanning] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<ReadLog | null>(null);
  const [showLog, setShowLog] = useState(false);

  async function toggleLog() {
    if (!showLog) {
      try {
        setLog(await queries.getReadLog());
      } catch {
        /* ignore */
      }
    }
    setShowLog((s) => !s);
  }

  async function scan() {
    setScanning(true);
    setError(null);
    setNote(null);
    try {
      const s = await queries.scanInbox();
      setNote(`Scanned ${s.scanned} (read ${s.candidates}, dropped ${s.dropped}) → ${s.suggestions} suggestion(s).`);
      sugs.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  }

  async function resolve(s: SuggestionRow, accept: boolean) {
    if (resolving) return;
    setResolving(s.id);
    setError(null);
    try {
      if (accept) await queries.acceptSuggestion(s.id);
      else await queries.dismissSuggestion(s.id);
      if (accept) onApplied();
    } catch (e) {
      // Already resolved or transient — a refetch clears it from the list.
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      sugs.refetch();
      setResolving(null);
    }
  }

  const items = sugs.data?.suggestions ?? [];

  return (
    <div className="mb-6 rounded-lg border bg-muted/20 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium">Inbox suggestions</h2>
          <p className="text-xs text-muted-foreground">
            Scan your mailbox for application updates. Nothing changes until you accept.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="text-xs text-primary hover:underline" onClick={toggleLog}>
            {showLog ? 'Hide' : 'What Rolefit read'}
          </button>
          <Button size="sm" variant="outline" disabled={scanning} onClick={scan}>
            {scanning ? 'Scanning…' : 'Scan inbox'}
          </Button>
        </div>
      </div>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
      {items.length > 0 && (
        <ul className="mt-3 space-y-2">
          {items.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-3 rounded-md border bg-background p-3 text-sm"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{s.company ?? 'Role'}</span>
                  {s.is_new && <Badge variant="outline">new role</Badge>}
                  {s.proposed_state && (
                    <Badge variant="secondary">→ {STATE_LABELS[s.proposed_state] ?? s.proposed_state}</Badge>
                  )}
                  <span className="text-xs text-muted-foreground">{Math.round(s.confidence * 100)}%</span>
                </div>
                <div className="truncate text-xs text-muted-foreground">{s.summary || s.subject}</div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" disabled={resolving !== null} onClick={() => resolve(s, true)}>
                  {resolving === s.id ? 'Applying…' : 'Accept'}
                </Button>
                <Button size="sm" variant="ghost" disabled={resolving !== null} onClick={() => resolve(s, false)}>
                  Dismiss
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showLog && log && (
        <div className="mt-3 rounded-md border bg-background p-3 text-xs">
          <p className="mb-2 text-muted-foreground">
            Read {log.relevant} application email{log.relevant === 1 ? '' : 's'} of {log.total} scanned; dropped{' '}
            {log.dropped} as bulk/newsletter (no content stored).
          </p>
          {log.items.length > 0 ? (
            <ul className="space-y-1.5">
              {log.items.map((it, i) => (
                <li key={i} className="flex items-center justify-between gap-2 border-t pt-1.5">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{it.subject ?? '(no subject)'}</div>
                    <div className="truncate text-muted-foreground">{it.from}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {it.type && <Badge variant="outline">{it.type.replace('_', ' ')}</Badge>}
                    <span className="text-muted-foreground">
                      {it.suggestion === 'accepted'
                        ? 'applied'
                        : it.suggestion === 'pending'
                          ? 'suggested'
                          : it.suggestion === 'dismissed'
                            ? 'dismissed'
                            : 'no update'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground">No application emails classified yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

function JdLink({ app }: { app: ApplicationRow }) {
  if (!app.source_url) return null;
  return (
    <a
      href={app.source_url}
      target="_blank"
      rel="noreferrer"
      className="text-xs text-primary hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      View JD
    </a>
  );
}

const toDateInput = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10) : '');

// A due date is overdue once its calendar day is before today (date-only compare).
function isOverdue(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(iso.slice(0, 10)).getTime() < today.getTime();
}

// Set/clear a "next action" + due date on an application. Collapsed until edited
// so the board stays scannable; overdue reminders are flagged in red.
function ReminderControl({ app, onSaved }: { app: ApplicationRow; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [next, setNext] = useState(app.next_action ?? '');
  const [due, setDue] = useState(toDateInput(app.due_ts));
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await queries.updateApplication(app.application_id, {
        next_action: next.trim() || null,
        due_ts: due ? new Date(due).toISOString() : null,
      });
      setEditing(false);
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    const has = app.next_action || app.due_ts;
    const overdue = isOverdue(app.due_ts);
    return (
      <div className="flex items-center justify-between gap-2 text-xs">
        {has ? (
          <span className={cn('truncate', overdue ? 'font-medium text-destructive' : 'text-muted-foreground')}>
            {app.next_action ?? 'Follow up'}
            {app.due_ts ? ` · ${new Date(app.due_ts).toLocaleDateString()}` : ''}
            {overdue ? ' (overdue)' : ''}
          </span>
        ) : (
          <span className="text-muted-foreground">No reminder</span>
        )}
        <button type="button" className="shrink-0 text-primary hover:underline" onClick={() => setEditing(true)}>
          {has ? 'Edit' : 'Set'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Input value={next} onChange={(e) => setNext(e.target.value)} placeholder="Next action" className="h-7 text-xs" />
      <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-7 text-xs" />
      <div className="flex gap-1.5">
        <Button size="sm" className="h-6 px-2 text-xs" disabled={busy} onClick={save}>
          Save
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          disabled={busy}
          onClick={() => {
            setNext(app.next_action ?? '');
            setDue(toDateInput(app.due_ts));
            setEditing(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function BoardPage() {
  const apps = useAsync(() => queries.listApplications(), []);
  const [view, setView] = useState<'board' | 'list'>('board');

  async function advance(app: ApplicationRow, to: string) {
    await queries.updateApplication(app.application_id, { state: to });
    apps.refetch();
  }

  const byState = (state: string) => (apps.data ?? []).filter((a) => a.state === state);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Board</h1>
          <p className="text-sm text-muted-foreground">Track each application through its stages.</p>
        </div>
        <div className="inline-flex rounded-md border p-0.5 text-sm">
          {(['board', 'list'] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={cn(
                'rounded px-3 py-1 capitalize',
                view === v ? 'bg-secondary font-medium' : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={() => setView(v)}
            >
              {v}
            </button>
          ))}
        </div>
      </header>

      <InboxSuggestions onApplied={() => apps.refetch()} />

      {apps.loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {apps.error && <p className="text-sm text-destructive">{apps.error}</p>}

      {view === 'board' ? (
        <div
          className="grid grid-flow-col gap-4 overflow-x-auto pb-4"
          style={{ gridAutoColumns: 'minmax(220px, 1fr)' }}
        >
          {BOARD_STATES.map((state) => {
            const items = byState(state);
            const next = BOARD_STATES[BOARD_STATES.indexOf(state) + 1];
            return (
              <div key={state} className="flex flex-col gap-3">
                <div className="flex items-center justify-between px-1">
                  <span className="text-sm font-medium">{STATE_LABELS[state]}</span>
                  <Badge variant="secondary">{items.length}</Badge>
                </div>
                {items.map((app) => (
                  <Card key={app.application_id}>
                    <CardContent className="space-y-2 p-3">
                      <div className="text-sm font-medium">{app.title ?? 'Role'}</div>
                      <div className="text-xs text-muted-foreground">{app.company}</div>
                      <JdLink app={app} />
                      <ReminderControl app={app} onSaved={() => apps.refetch()} />
                      {next && (
                        <Button size="sm" variant="outline" className="w-full" onClick={() => advance(app, next)}>
                          Move to {STATE_LABELS[next]}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">Company</th>
                <th className="px-4 py-2 font-medium">Stage</th>
                <th className="px-4 py-2 font-medium">Next action</th>
                <th className="px-4 py-2 font-medium">Due</th>
                <th className="px-4 py-2 font-medium">JD</th>
              </tr>
            </thead>
            <tbody>
              {(apps.data ?? []).map((app) => (
                <tr key={app.application_id} className="border-t">
                  <td className="px-4 py-3 font-medium">{app.title ?? 'Role'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{app.company ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{STATE_LABELS[app.state] ?? app.state}</Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{app.next_action ?? '—'}</td>
                  <td
                    className={cn(
                      'px-4 py-3',
                      isOverdue(app.due_ts) ? 'font-medium text-destructive' : 'text-muted-foreground',
                    )}
                  >
                    {app.due_ts ? new Date(app.due_ts).toLocaleDateString() : '—'}
                    {isOverdue(app.due_ts) ? ' (overdue)' : ''}
                  </td>
                  <td className="px-4 py-3">
                    <JdLink app={app} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!apps.loading && (apps.data ?? []).length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              No applications yet. Track a role from the Pipeline.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
